import type { CartItem } from '#types/cart'
import type { FilterBy, FilterByContent, Operator, Type } from '#types/components/cms/filter-by'
import type { Content } from '#types/content'
import type { RouteLocationNormalizedLoadedGeneric } from 'vue-router'

type VariantsMap = Record<Type, (data: string) => boolean>
type OperatorMapping = Record<Operator, (input: boolean) => boolean>

const operatorMapping: OperatorMapping = {
  'IN': (input: boolean) => input,
  'NOT IN': (input: boolean) => !input
}

const normalizeList = (data: string, separator: RegExp = /\n+/) => data
  ?.split(separator)
  .map((s) => s.trim().replace(/\n/g, ''))
  .filter(Boolean) || []

/**
 * Checks whether any filter in a `FilterByContent` depends on cart state.
 *
 * @remarks
 * Used to decide whether a caller needs to defer to `useSsrSafeFilterState` - cart state is
 * only reliably known client-side, so content with no `byCart` filter can skip that entirely and
 * stay fully SSR-rendered.
 * @param filters - The `filters` array from a `FilterByContent` instance (or `undefined`).
 * @returns `true` when at least one filter has `type: 'byCart'`.
 * @category Utils
 */
export const hasByCartFilter = (filters?: FilterBy[]): boolean =>
  filters?.some(({ type }) => type === 'byCart') ?? false

/**
 * Evaluates a single attribute expression against a map of attributes.
 *
 * @param expression - A string in the form `key=value` (exact match) or `key~value` (fuzzy / includes match).
 * @param attributes - The attributes map to look up the key in.
 * @returns `true` when the attribute satisfies the expression, `false` otherwise.
 */
const matchAttribute = (expression: string, attributes: Record<string, string> = {}): boolean => {
  if (!attributes) return false
  const fuzzy = expression.includes('~')
  const [key, value] = expression.split(fuzzy ? '~' : '=').map((s) => s.trim())
  const attrValue = attributes[key]
  if (!attrValue) return false
  return fuzzy ? attrValue.includes(value) : attrValue === value
}

/**
 * Filters an array of CMS components based on the given configuration which is compared against current route's path and query parameters.
 *
 * @remarks
 * This function acts as a dynamic content filtering engine, designed to power CMS-driven
 * pages. It processes a list of filter rules, each associated with a set of components.
 * It evaluates each rule against the current `route` to determine if its components
 * should be included in the final output.
 *
 * The filtering logic is determined by a combination of:
 * - `type`: The context to check (e.g., `'bySku'`, `'byCategory'`, `'byQuery'`).
 * - `filterBy`: The data for the check (e.g., a comma-separated list of SKUs, a query string).
 * - `operator`: The logical condition (`'IN'` or `'NOT IN'`).
 *
 * @param {FilterByContent} [content] - FilterByContent instance, which contains an array of filter configuration objects.
 * Each object defines a condition and the `components` to render if that condition passes
 * and baseline - an array of CMS components, that is rendered if no filters matched.
 * @param {object} data - Contextual data for filter evaluation.
 * @param {RouteLocationNormalizedLoadedGeneric} data.route - The active `vue-router` route object, which provides the context (path, query) for evaluation.
 * @param {Record<string, string>} data.attributes - A map of content attributes for attribute-based filtering.
 * @param {CartItem[]} data.cartItems - The current cart's items, used for `byCart` filtering against `masterId` / `primaryCategoryId`.
 * @returns {Content[]} A new, flattened array containing all `Content` components that satisfy their respective filtering conditions for the current route.
 */
export default (
  { filters = [], baseline = [] }: FilterByContent,
  { route, attributes, cartItems }: {
    route: RouteLocationNormalizedLoadedGeneric
    attributes?: Record<string, string>
    cartItems?: CartItem[]
  }
): Content[] => {
  const variantsMap: VariantsMap = {
    bySku: (data: string) =>
      normalizeList(data, /[,\n]+/).includes(getUrlId(route.path, 'Product')),
    byCategory: (data: string) =>
      normalizeList(data, /[,\n]+/).includes(getUrlId(route.path, 'Category')),
    byQuery: (data: string) =>
      normalizeList(data).some((split: string) => matchQuery(split, route.query)),
    byPath: (data: string) =>
      normalizeList(data).some((url: string) => url.split('*').every((part) => route.fullPath.includes(part))),
    byAttribute: (data: string) =>
      normalizeList(data).some((expression: string) => matchAttribute(expression, attributes)),
    byCart: (data: string) =>
      normalizeList(data, /[,\n]+/).some((split: string) => cartItems?.some((item) => item.masterId.includes(split) || item.primaryCategoryId === split))
  }

  const filteredContents = filters
    .flatMap(
      ({ type, filterBy, operator, components = [] }) =>
        operatorMapping[operator]?.(variantsMap[type]?.(filterBy)) ? components : []
    )

  return filteredContents.length ? filteredContents : baseline
}
